import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ChevronLeft, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrg } from '@/hooks/useCurrentOrg';

interface Course {
  id: string;
  name: string;
  description: string | null;
  grade: string | null;
  subject: string | null;
}

interface Lesson {
  id: string;
  title: string;
  description: string | null;
  position: number;
}

export function StudentCourses() {
  const { currentOrg, loading: orgLoading } = useCurrentOrg();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgLoading) return;
    if (!currentOrg) {
      setLoading(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('courses')
        .select('id, name, description, grade, subject')
        .eq('org_id', currentOrg.org_id)
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      setCourses(data ?? []);
      setLoading(false);
    })();
  }, [currentOrg, orgLoading]);

  if (loading || orgLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-10">
      <Helmet>
        <title>My courses</title>
        <meta name="description" content="Your enrolled courses." />
      </Helmet>
      <h1 className="text-2xl font-semibold tracking-tight mb-6">Courses</h1>
      {courses.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <BookOpen className="mx-auto mb-3 h-8 w-8 opacity-40" />
            No published courses yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {courses.map((c) => (
            <Link
              key={c.id}
              to={`/student/courses/${c.id}`}
              className="block rounded-lg border bg-card p-4 hover:bg-accent transition-colors"
            >
              <div className="font-medium">{c.name}</div>
              {c.description && (
                <div className="text-sm text-muted-foreground">{c.description}</div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                {c.grade && <Badge variant="secondary">Grade {c.grade}</Badge>}
                {c.subject && <Badge variant="secondary">{c.subject}</Badge>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function StudentCourseView() {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!courseId) return;
    (async () => {
      const { data: c } = await supabase
        .from('courses')
        .select('id, name, description, grade, subject')
        .eq('id', courseId)
        .is('deleted_at', null)
        .maybeSingle();
      setCourse(c ?? null);

      const { data: ls } = await supabase
        .from('course_lessons')
        .select('id, title, description, position')
        .eq('course_id', courseId)
        .eq('is_published', true)
        .is('deleted_at', null)
        .order('position', { ascending: true });
      setLessons(ls ?? []);
      setLoading(false);
    })();
  }, [courseId]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="container max-w-2xl py-10">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Course unavailable.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-10">
      <Helmet>
        <title>{course.name}</title>
        <meta name="description" content={course.description ?? course.name} />
      </Helmet>

      <Link
        to="/student/courses"
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ChevronLeft className="h-4 w-4 mr-1" /> All courses
      </Link>

      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{course.name}</h1>
        {course.description && (
          <p className="text-sm text-muted-foreground mt-1">{course.description}</p>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lessons</CardTitle>
        </CardHeader>
        <CardContent className="p-0 divide-y">
          {lessons.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No lessons published yet.
            </div>
          ) : (
            lessons.map((l, i) => (
              <Link
                key={l.id}
                to={`/student/lessons/${l.id}`}
                className="flex items-center gap-3 p-4 hover:bg-accent"
              >
                <div className="text-xs text-muted-foreground w-6">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{l.title}</div>
                  {l.description && (
                    <div className="text-sm text-muted-foreground truncate">
                      {l.description}
                    </div>
                  )}
                </div>
              </Link>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
